import PlaceSearchWithSavedLocations from "../../../PlaceSearchWithSavedLocations/PlaceSearchWithSavedLocations";
import { UFDoraTimeRangePicker, UFDoraTextInput, UFDoraNumberInput, UFDoraCheckbox, UFDoraSelect, UFDoraDatePicker, DoraButton, DoraLinkButton } from "dora-shared/frontend";
import { UFDoraMultilineTextAutocomplete } from "../../../Toolkit/DoraMultilineTextAutocomplete";
import { loadDocuments } from "../../../../ducks/data/stop-documents";
import {
  faCircleUser,
  faComment,
  faNoteSticky,
  faTrashAlt,
} from "@fortawesome/free-regular-svg-icons";
import {
  faGauge,
  faPhone,
  faPlus,
  faTag,
  // faTag,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { Alert } from "@mui/material";
import "./StopEdit.scss";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AddressChange } from "../../../NewPlaceSearch";
import ManageSavedLocationsDialog from "../../ManageSavedLocationsDialog";
import { useFieldArray, useFormContext } from "react-hook-form";
import { get as lodashGet } from "lodash";
import GenericPromptDialog from "../../GenericPromptDialog/GenericPromptDialog";
import { getUnits } from "../../../../helpers/types";
import { useTranslation } from "react-i18next";
import { nanoid } from "nanoid";
import { v4 as uuid } from "uuid";
import {
  StopPlace,
  StopUnit,
} from "../../../../ducks/app/cargo-and-template-shared";
import { focusOnLocationSearchInput } from "./helpers";
import { useFeature } from "../../../../hooks";
import Dropzone from "react-dropzone";
import StopDocumentsList from "./StopDocumentsList";
import { useAppDispatch, useSelector } from "../../../../redux-store";
import axios from "../../../../axios";
import { notify } from "../../../../ducks/notifications";
import { CloseButton } from "../../../dispatching-planning/common/close-button";
import { selectHideLoadmeters } from "../../../../ducks/data/org-settings/selectors";
import { CopyIconButton } from "../../../buttons";
import { timeRangeMin5MinutesErr } from "../create-edit-shipment-validation-schema";
import { get as _get } from "lodash";
import axiosRaw from "axios";
import {
  selectAllStopUnitTemplates,
  selectMyTeamsStopUnitTemplates,
} from "../../../../ducks/data/stop-unit-templates/selectors";
import { selectResolvedDeliveryLocations } from "../../../../ducks/data/economics/customer-delivery-locations/selectors";
import StopUnitContent from "./StopUnitContent";
import Decimal from "decimal.js-light";
import StopUnitRow from "./StopUnitRow";
import AssignStopUnitsDialog from "./AssignStopUnitsDialog";
import useDropEmptyFiles from "../documents/use-drop-empty-files";

type StopEditProps = {
  disableAccumulatedInputs?: boolean;
  isPickup?: boolean;
  isSingleStop?: boolean;
  // See CreateEditShipmentDialogProps.initialDataFromCache
  stopMayReferenceStaleData?: boolean;

  // useForms inject
  relativeFormPath: string;
  errors: any;
};

const StopEdit = (props: StopEditProps) => {
  const [
    shouldShowConfirmDeleteUnitsDialog,
    setShouldShowConfirmDeleteUnitsDialog,
  ] = useState(false);
  const [
    shouldShowConfirmDeleteMeasurementsDialog,
    setShouldShowConfirmDeleteMeasurementsDialog,
  ] = useState(false);
  const [showManageSavedLocations, setShowManageSavedLocations] =
    useState(false);
  const [
    measurementsDisplayButtonEnabled,
    setMeasurementsDisplayButtonEnabled,
  ] = useState(false);
  const [creatingNewStopUnit, setCreatingNewStopUnit] = useState(false);
  const [editingStopUnitIds, setEditingStopUnitIds] = useState<string[]>([]);
  const onStopUnitEditingChange = useCallback(
    (unitId: string, isEditing: boolean) => {
      setEditingStopUnitIds((prev) =>
        isEditing
          ? prev.includes(unitId)
            ? prev
            : [...prev, unitId]
          : prev.filter((id) => id !== unitId)
      );
    },
    []
  );
  const [showAssignUnitsModal, setShowAssignUnitsModal] = useState(false);
  const [placeInputType, setPlaceInputType] = useState<
    "LOCATION" | "COORDS" | null
  >(null);
  const chooseSearchTypeCoordsEnabled = useFeature(
    "dispatcher-place-coords-only"
  );
  const liteVersion = useFeature("lite-version");
  const stopUnitsFeatureEnabled = useFeature("DO NOT TOUCH new-stops-units");
  const serverAutocompleteEnabled = useFeature(
    "user-shipment-fields-autocomplete-from-server"
  );
  const {
    isPickup,
    disableAccumulatedInputs,
    errors,
    relativeFormPath,
    isSingleStop,
    stopMayReferenceStaleData,
  } = props;
  const { watch, setFocus, register, control, getValues, setValue } =
    useFormContext();
  const { t } = useTranslation([
    "types",
    "shipmentModal",
    "components",
    "stopUnits",
  ]);
  const {
    fields: units,
    append: appendField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: `${relativeFormPath}.pieces`,
  });
  const {
    append: appendStopUnit,
    update: updateStopUnit,
    remove: removeStopUnit,
  } = useFieldArray({
    control,
    name: "stopUnits",
  });
  const stopId = getValues(`${relativeFormPath}.id`);
  const provisoryId = getValues(`${relativeFormPath}.provisoryId`);

  // Use id for existing stops, provisoryId for new stops
  const currentStopReferenceId: string = stopId || provisoryId;

  const [documentsChangeInProgress, setDocumentsChangeInProgress] =
    useState(false);
  const dispatch = useAppDispatch();
  const dropEmptyFiles = useDropEmptyFiles();
  const hideLoadmeters = useSelector(selectHideLoadmeters);
  const customerDeliveryAddresses = useSelector(
    selectResolvedDeliveryLocations
  );
  const clientId = watch("clientId");

  // const pickupListWatch = watch("pickupList");
  // const dropoffListWatch = watch("dropoffList");
  const stopUnitsWatch = watch("stopUnits") as StopUnit[];

  const unitsTypes = useMemo(
    () =>
      getUnits().map(({ id }) => ({
        value: id,
        label: t("types:unitTypes.unit", { context: id }),
      })),
    [t]
  );

  const shouldDisableTimeWhenArrived = useFeature(
    "arrived-departed-trigger-stop-times-updates"
  );
  const arrivedSet = watch(`${relativeFormPath}.arrivedDate`);

  const timeFieldDisabled = useMemo(
    () => arrivedSet && shouldDisableTimeWhenArrived,
    [shouldDisableTimeWhenArrived, arrivedSet]
  );

  const onAddUnitsButtonClick = () => {
    appendField({ exchange: false });
    setTimeout(() => {
      setFocus(`${relativeFormPath}.pieces[${units.length}].quantity`);
    }, 50);
  };

  const onDeleteUnitsClick = () => {
    setShouldShowConfirmDeleteUnitsDialog(true);
  };

  const onConfirmDeleteUnitsClick = () => {
    for (let i = 0; i < units.length; i++) {
      removeField(0);
    }
    setShouldShowConfirmDeleteUnitsDialog(false);
  };

  const onDeleteUnitClick = (index: number) => () => {
    removeField(index);
  };

  const onMeasurementsButtonClick = () => {
    setMeasurementsDisplayButtonEnabled(true);
    setTimeout(() => setFocus(`${relativeFormPath}.colli`), 50);
  };

  const onConfirmDeleteMeasurementsClick = () => {
    setValue(`${relativeFormPath}.colli`, "");
    setValue(`${relativeFormPath}.weight`, "");
    setValue(`${relativeFormPath}.length`, "");
    setValue(`${relativeFormPath}.width`, "");
    setValue(`${relativeFormPath}.height`, "");
    setValue(`${relativeFormPath}.cubicMeters`, "");
    setShouldShowConfirmDeleteMeasurementsDialog(false);
    setMeasurementsDisplayButtonEnabled(false);
  };

  const allUnitTemplates = useSelector(selectAllStopUnitTemplates);

  const onDeleteMeasurementsClick = () => {
    setShouldShowConfirmDeleteMeasurementsDialog(true);
  };

  const onPlaceSelected = (
    p: null | AddressChange,
    extraProps?: {
      contactName: string;
      contactPhoneNumber: string;
      openingTime: string;
    }
  ) => {
    const place: StopPlace | null = p
      ? {
          placeName: p.placeName || p.place || "",
          postcode: p.postcode,
          city: p.city,
          country: p.country,
          address: p.address,
          coord: p.coord,
          deliveryAddressId: p.deliveryAddressId,
        }
      : null;
    setValue(`${relativeFormPath}.place`, place);
    if (extraProps) {
      setValue(`${relativeFormPath}.contact`, extraProps.contactName);
      setValue(
        `${relativeFormPath}.contactPhone`,
        extraProps.contactPhoneNumber
      );
      setValue(`${relativeFormPath}.openingTime`, extraProps.openingTime);
    }
    const dateInput = document.querySelector(`#${dateWrapperId} input`) as any;
    if (dateInput) {
      dateInput.focus();
    }
  };

  const onUploadDocumentsClick = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      // this only happens when a drag drop operation is done with an unaccepted file type
      dispatch(
        notify({
          message: t(
            `shipmentModal:cards.documents.notificationErrorNotAccepted`
          ),
          type: "error",
        })
      );
      return;
    }
    const files = dropEmptyFiles(acceptedFiles);
    if (!files.length) {
      return;
    }
    setDocumentsChangeInProgress(true);

    try {
      const fileReferences = [];
      for (const file of files) {
        // Step 1: Request signed URL from server
        const urlResponse = await axios.post(
          `/api/cargos/document-upload-url`,
          {
            contentType: file.type,
          }
        );
        const { uploadUrl, s3Identifier } = urlResponse.data;

        // Step 2: Upload a file directly to S3 (use raw axios without custom headers)
        const buffer = await file.arrayBuffer();
        await axiosRaw.put(uploadUrl, buffer, {
          headers: {
            "Content-Type": file.type,
          },
        });

        // Collect file reference data
        fileReferences.push({
          s3Identifier,
          name: file.name,
          mimeType: file.type,
        });
      }

      // Step 3: Save file references in database
      await axios.post(`/api/stops/${stopId}/documents-reference`, {
        files: fileReferences,
      });

      dispatch(loadDocuments({ stopId: stopId as string, force: true }));
      dispatch(
        notify({
          message: t(`shipmentModal:cards.documents.notificationSuccess`, {
            count: files.length,
          }),
          type: "success",
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(
        notify({
          message: t(`shipmentModal:cards.documents.notificationError`, {
            count: files.length,
          }),
          type: "error",
        })
      );
    } finally {
      setDocumentsChangeInProgress(false);
    }
  };

  const placeWatch = watch(`${relativeFormPath}.place`);
  const unitsWatch = watch(`${relativeFormPath}.pieces`);

  const onChangeAddressClick = () => {
    setValue(`${relativeFormPath}.place`, null);
    focusOnLocationSearchInput(`${relativeFormPath}`);
  };

  const onManageLocationsClick = () => {
    setShowManageSavedLocations(true);
  };

  const currentStopErrors = lodashGet(errors, relativeFormPath) || {};

  const dateId = useMemo(() => nanoid(), []);
  const dateWrapperId = useMemo(() => "id-" + nanoid(), []);
  const tagId = useMemo(() => nanoid(), []);
  const driverNoteId = useMemo(() => nanoid(), []);
  const lmId = useMemo(() => nanoid(), []);
  const colliId = useMemo(() => nanoid(), []);
  const weightId = useMemo(() => nanoid(), []);
  const lengthId = useMemo(() => nanoid(), []);
  const widthId = useMemo(() => nanoid(), []);
  const cubicMetersId = useMemo(() => nanoid(), []);
  const heightId = useMemo(() => nanoid(), []);
  const contactId = useMemo(() => nanoid(), []);
  const phoneId = useMemo(() => nanoid(), []);
  const loadingRefId = useMemo(() => nanoid(), []);
  const searchId = useMemo(() => nanoid(), []);
  const fixId = useMemo(() => nanoid(), []);

  const shouldShowMeasurements = !!(
    measurementsDisplayButtonEnabled ||
    Number(watch(`${relativeFormPath}.colli`)) ||
    Number(watch(`${relativeFormPath}.weight`)) ||
    Number(watch(`${relativeFormPath}.length`)) ||
    Number(watch(`${relativeFormPath}.width`)) ||
    Number(watch(`${relativeFormPath}.height`)) ||
    Number(watch(`${relativeFormPath}.cubicMeters`))
  );

  const fullAddress = `${
    watch(`${relativeFormPath}.place.placeName`) &&
    `${watch(`${relativeFormPath}.place.placeName`)}, `
  } ${
    watch(`${relativeFormPath}.place.address`) &&
    `${watch(`${relativeFormPath}.place.address`)}, `
  } ${watch(`${relativeFormPath}.place.postcode`)} ${watch(
    `${relativeFormPath}.place.city`
  )}`;

  const onSwitchPlaceInputTypeClick = () => {
    const newInputType = placeInputType === "COORDS" ? "LOCATION" : "COORDS";
    if (newInputType === "COORDS") {
      setValue(`${relativeFormPath}.place`, {
        coord: { lat: null, lon: null },
      });
    } else {
      setValue(`${relativeFormPath}.place`, null);
    }
    setPlaceInputType(newInputType);
  };

  const latError = useMemo(
    () => _get(errors, relativeFormPath)?.place?.coord?.lat,
    [errors, relativeFormPath]
  );
  const lonError = useMemo(
    () => _get(errors, relativeFormPath)?.place?.coord?.lon,
    [errors, relativeFormPath]
  );

  useEffect(() => {
    if (placeInputType !== null) {
      return;
    }
    const data = getValues(`${relativeFormPath}.place`);
    if (data?.coord?.lat && !data.country) {
      setPlaceInputType("COORDS");
    } else {
      setPlaceInputType("LOCATION");
    }
  }, [getValues, placeInputType, setPlaceInputType, relativeFormPath]);

  const onLatitudePaste = (value: string) => {
    const latLon = value.split(",");
    if (latLon.length > 2) {
      return;
    }
    const lat = latLon[0].trim();
    const latNumber = parseFloat(lat);
    if (!isNaN(latNumber)) {
      setValue(`${relativeFormPath}.place.coord.lat`, lat);
    }

    if (latLon.length < 2) {
      return;
    }
    const lon = latLon[1].trim();
    const lonNumber = parseFloat(lon);
    if (!isNaN(lonNumber)) {
      setValue(`${relativeFormPath}.place.coord.lon`, lon);
    }
  };

  // Stop Units helpers
  const currentStopUnits = useMemo(() => {
    if (!stopUnitsWatch) return [];
    if (isPickup) {
      return stopUnitsWatch.filter(
        (su: StopUnit) => su.pickupId === currentStopReferenceId
      );
    } else {
      return stopUnitsWatch.filter(
        (su: StopUnit) => su.dropoffId === currentStopReferenceId
      );
    }
  }, [stopUnitsWatch, currentStopReferenceId, isPickup]);

  const availablePickupUnits = useMemo(() => {
    if (!stopUnitsWatch || isPickup) return [];
    return stopUnitsWatch.filter((su: StopUnit) => su.dropoffId === null);
  }, [stopUnitsWatch, isPickup]);

  useEffect(() => {
    if (showAssignUnitsModal && availablePickupUnits.length === 0) {
      setShowAssignUnitsModal(false);
    }
  }, [availablePickupUnits.length, showAssignUnitsModal]);

  const onCreateStopUnitClick = () => {
    setCreatingNewStopUnit(true);
  };

  const onSaveStopUnit = async (
    unitData: StopUnit,
    stopUnitIndex: number | null
  ) => {
    if (stopUnitIndex === null) {
      const newUnit: StopUnit = {
        ...unitData,
        // A UUID: the server keeps it, so the unit keeps its id across saves
        // and an open dialog recognises it when merging others' changes
        // (DORA-441).
        id: uuid(),
        pickupId: currentStopReferenceId,
        dropoffId: null,
        quantity: unitData.quantity || new Decimal(1),
      };
      appendStopUnit(newUnit);
      setCreatingNewStopUnit(false);
    } else {
      const existingUnit = stopUnitsWatch[stopUnitIndex];
      updateStopUnit(stopUnitIndex, {
        ...existingUnit,
        weight: unitData.weight,
        width: unitData.width,
        length: unitData.length,
        height: unitData.height,
        volume: unitData.volume,
        colli: unitData.colli,
        quantity: unitData.quantity || new Decimal(1),
        unitId: unitData.stopUnitTemplateId,
        description: unitData.description || "",
      });
    }
  };

  const userSelectableStopUnitTemplates = useSelector(
    selectMyTeamsStopUnitTemplates
  );

  const newUnit: Omit<StopUnit, "pickupId"> = {
    id: "NEW",
    quantity: null,
    weight: null,
    width: null,
    length: null,
    height: null,
    volume: null,
    stopUnitTemplateId: undefined as any, // hack for not messing with types more
    description: "",
    colli: null,
    dropoffId: null,
  };

  const onAssignUnitToDropoff = (unitIndex: number) => {
    const unit = stopUnitsWatch[unitIndex];
    updateStopUnit(unitIndex, {
      ...unit,
      dropoffId: currentStopReferenceId,
    });
  };

  const onRemoveUnitFromDropoff = (unitIndex: number) => {
    const unit = stopUnitsWatch[unitIndex];
    updateStopUnit(unitIndex, {
      ...unit,
      dropoffId: null,
    });
  };

  if (placeInputType === null) {
    return null;
  }

  return (
    <>
      {shouldShowConfirmDeleteMeasurementsDialog && (
        <GenericPromptDialog
          title={t("shipmentModal:removeMeasurementsModal.title")}
          text={t("shipmentModal:removeMeasurementsModal.description")}
          continueText={t("components:buttonLabels.remove")}
          continueButtonColor={"error"}
          onCancelClick={() =>
            setShouldShowConfirmDeleteMeasurementsDialog(false)
          }
          onContinueClick={onConfirmDeleteMeasurementsClick}
        />
      )}
      {shouldShowConfirmDeleteUnitsDialog && (
        <GenericPromptDialog
          title={t("shipmentModal:removeUnitsModal.title")}
          text={t("shipmentModal:removeUnitsModal.description")}
          continueText={t("components:buttonLabels.remove")}
          continueButtonColor={"error"}
          onCancelClick={() => setShouldShowConfirmDeleteUnitsDialog(false)}
          onContinueClick={onConfirmDeleteUnitsClick}
        />
      )}
      {showManageSavedLocations && (
        <ManageSavedLocationsDialog
          onClose={() => setShowManageSavedLocations(false)}
          customerDeliveryAddresses={
            !!clientId ? customerDeliveryAddresses : []
          }
          clientId={clientId || undefined}
        />
      )}
      <div className="shipment-cargo-form">
        {placeWatch && placeInputType === "LOCATION" ? (
          <div className="shipment-cargo-form__place">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {isPickup && !isSingleStop
                  ? t("shipmentModal:inputs.searchLoadingPlaceLabel")
                  : isSingleStop
                    ? t("shipmentModal:inputs.searchPlaceLabel")
                    : t("shipmentModal:inputs.searchUnloadingPlaceLabel")}
                <CopyIconButton
                  label={fullAddress}
                  title={t("components:buttonLabels.copyFullAddress")}
                  noPadding
                />
              </div>
              <div>
                <DoraLinkButton onClick={onManageLocationsClick}>
                  {t("shipmentModal:inputs.searchPlaceManage")}
                </DoraLinkButton>
              </div>
            </div>
            <div>
              <div style={{ position: "relative" }}>
                <UFDoraTextInput
                  register={register(`${relativeFormPath}.place.placeName`)}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlacePlaceNamePlaceholder"
                  )}
                />
                <div style={{ position: "absolute", top: 8, right: 12 }}>
                  <CloseButton onClick={onChangeAddressClick} />
                </div>
              </div>
            </div>
            <div>
              {serverAutocompleteEnabled ? (
                <UFDoraMultilineTextAutocomplete
                  name={`${relativeFormPath}.place.address`}
                  autocompleteKey="stopAddress"
                  control={control}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlaceAddressPlaceholder"
                  )}
                />
              ) : (
                <UFDoraTextInput
                  register={register(`${relativeFormPath}.place.address`)}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlaceAddressPlaceholder"
                  )}
                  autoResize
                />
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 3fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <UFDoraTextInput
                  disabled
                  register={register(`${relativeFormPath}.place.postcode`)}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlaceZipPlaceholder"
                  )}
                />
              </div>
              <div>
                <UFDoraTextInput
                  disabled
                  register={register(`${relativeFormPath}.place.city`)}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlaceCityPlaceholder"
                  )}
                />
              </div>
              <div>
                <UFDoraTextInput
                  disabled
                  register={register(`${relativeFormPath}.place.country`)}
                  placeholder={t(
                    "shipmentModal:inputs.searchPlaceCountryPlaceholder"
                  )}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            id={"search-wrapper-" + relativeFormPath}
            className="shipment-cargo-form__place-search"
          >
            <div className="flex justify-between items-center">
              <label htmlFor={searchId}>
                {placeInputType === "LOCATION"
                  ? t("shipmentModal:inputs.searchPlaceLabel2")
                  : t("shipmentModal:inputs.switchCoordsLabel")}
                <span aria-hidden>*</span>
              </label>
              {placeInputType === "LOCATION" && (
                <div>
                  <DoraLinkButton onClick={onManageLocationsClick}>
                    {t("shipmentModal:inputs.searchPlaceManage")}
                  </DoraLinkButton>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {placeInputType === "LOCATION" ? (
                <>
                  <div className="flex-grow">
                    <PlaceSearchWithSavedLocations
                      id={searchId}
                      noLabel
                      onPlaceSelected={onPlaceSelected}
                      placeError={currentStopErrors.place}
                      customerDeliveryAddresses={
                        !!clientId ? customerDeliveryAddresses : []
                      }
                    />
                  </div>
                  {chooseSearchTypeCoordsEnabled && !liteVersion && (
                    <DoraButton
                      variant="primary"
                      onClick={onSwitchPlaceInputTypeClick}
                    >
                      {t("shipmentModal:inputs.switchCoordsButton")}
                    </DoraButton>
                  )}
                </>
              ) : (
                <>
                  <div className="flex flex-col flex-grow gap-2">
                    <div className="flex gap-2">
                      <UFDoraNumberInput
                        className="flex-grow"
                        register={register(
                          `${relativeFormPath}.place.coord.lat`
                        )}
                        onPaste={onLatitudePaste}
                        error={!!latError}
                        placeholder={t(
                          "shipmentModal:inputs.latitudePlaceholder"
                        )}
                        disableUpDownArrows
                      />
                      <UFDoraNumberInput
                        className="flex-grow"
                        register={register(
                          `${relativeFormPath}.place.coord.lon`
                        )}
                        error={!!lonError}
                        placeholder={t(
                          "shipmentModal:inputs.longitudePlaceholder"
                        )}
                        disableUpDownArrows
                      />
                    </div>
                    <UFDoraTextInput
                      register={register(`${relativeFormPath}.place.placeName`)}
                      placeholder={t(
                        "shipmentModal:inputs.switchCoordsPlaceholder"
                      )}
                    />
                  </div>
                  <div>
                    <DoraButton
                      variant="primary"
                      onClick={onSwitchPlaceInputTypeClick}
                    >
                      {t("shipmentModal:inputs.switchPlaceSearchButton")}
                    </DoraButton>
                  </div>
                </>
              )}
            </div>
            {currentStopErrors.place && !currentStopErrors.place.coord && (
              <div className="shipment-cargo-form__error">
                {t(
                  `shipmentModal:errors.${currentStopErrors.place.message}` as any
                )}
              </div>
            )}
          </div>
        )}
        <div className="shipment-cargo-form__input-list">
          {!liteVersion && (
            <div className="shipment-cargo-form__input-list-row">
              <label className="shipment-cargo-form__input-list-row-label">
                {t("shipmentModal:inputs.openingTimeLabel")}
              </label>
              <div className="flex-grow">
                <div
                  className="flex items-center justify-between"
                  style={{ gap: 12 }}
                >
                  <div id={dateWrapperId} className="flex-grow">
                    <UFDoraDatePicker
                      id={dateId}
                      name={`${relativeFormPath}.openingDate`}
                      control={control}
                      error={currentStopErrors.openingDate}
                    />
                  </div>
                  <>
                    <div className="flex-grow">
                      <UFDoraTimeRangePicker
                        name={`${relativeFormPath}.openingTime`}
                        getValues={getValues}
                        setValue={setValue}
                      />
                    </div>
                    <div className="shipment-cargo-form__fix-input-wrapper">
                      <label
                        className="shipment-cargo-form__fix-text"
                        htmlFor={fixId}
                      >
                        {t("shipmentModal:inputs.fixLabel")}
                      </label>
                      <UFDoraCheckbox
                        id={fixId}
                        className="shipment-cargo-form__fix-input"
                        register={register(`${relativeFormPath}.fix`)}
                      />
                    </div>
                  </>
                </div>
              </div>
            </div>
          )}
          <div className="shipment-cargo-form__input-list-row">
            <label
              className="shipment-cargo-form__input-list-row-label"
              htmlFor={dateId}
            >
              <i>{t("shipmentModal:inputs.dateTimeLabel")}</i>
              <div className="shipment-cargo-form__info">
                {t("shipmentModal:inputs.dateTimeLabelHelperText")}
              </div>
            </label>
            <div className="flex-grow">
              <div
                className="flex items-center justify-between"
                style={{ gap: 12 }}
              >
                <div id={dateWrapperId} className="flex-grow">
                  <UFDoraDatePicker
                    id={dateId}
                    name={`${relativeFormPath}.date`}
                    control={control}
                    error={currentStopErrors.date}
                  />
                </div>
                <div className="flex-grow">
                  <UFDoraTimeRangePicker
                    name={`${relativeFormPath}.time`}
                    getValues={getValues}
                    setValue={setValue}
                    error={currentStopErrors.time}
                    disabled={timeFieldDisabled}
                  />
                  {currentStopErrors.time?.type === timeRangeMin5MinutesErr && (
                    <div className="shipment-cargo-form__error">
                      {t("shipmentModal:errors.timeRangeMin5Minutes")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {!liteVersion && (
            <>
              <div className="shipment-cargo-form__input-list-row">
                <label
                  className="shipment-cargo-form__input-list-row-label"
                  htmlFor={contactId}
                >
                  {t("shipmentModal:inputs.contactLabel")}
                </label>
                <UFDoraTextInput
                  id={contactId}
                  register={register(`${relativeFormPath}.contact`)}
                  placeholder={t("shipmentModal:inputs.contactPlaceholder2")}
                  className="flex-grow"
                  icon={faCircleUser}
                />
              </div>
              <div className="shipment-cargo-form__input-list-row">
                <label
                  className="shipment-cargo-form__input-list-row-label"
                  htmlFor={phoneId}
                >
                  {t("shipmentModal:inputs.phoneLabel")}
                </label>
                <UFDoraNumberInput
                  id={phoneId}
                  register={register(`${relativeFormPath}.contactPhone`)}
                  className="flex-grow"
                  placeholder={t("shipmentModal:inputs.phonePlaceholder2")}
                  icon={faPhone}
                  disableUpDownArrows
                />
              </div>
              <div className="shipment-cargo-form__input-list-row">
                <label
                  className="shipment-cargo-form__input-list-row-label"
                  htmlFor={loadingRefId}
                >
                  {isPickup && !isSingleStop
                    ? t("shipmentModal:inputs.loadingRefLabel")
                    : isSingleStop
                      ? t("shipmentModal:inputs.refLabel")
                      : t("shipmentModal:inputs.unloadingRefLabel")}
                </label>
                <UFDoraTextInput
                  id={loadingRefId}
                  className="flex-grow"
                  placeholder={t("shipmentModal:inputs.loadingRefPlaceholder")}
                  register={register(`${relativeFormPath}.ref`)}
                  icon={faNoteSticky}
                />
              </div>

              <div className="shipment-cargo-form__input-list-row">
                <label
                  htmlFor={tagId}
                  className="shipment-cargo-form__input-list-row-label"
                >
                  {t("shipmentModal:inputs.tagLabel")}
                </label>
                <UFDoraTextInput
                  id={tagId}
                  className="flex-grow"
                  placeholder={t("shipmentModal:inputs.tagPlaceholder")}
                  register={register(`${relativeFormPath}.tag`)}
                  icon={faTag}
                />
              </div>
            </>
          )}

          <div className="shipment-cargo-form__input-list-row">
            <label
              className="shipment-cargo-form__input-list-row-label"
              htmlFor={driverNoteId}
            >
              {t("shipmentModal:inputs.noteForDriver")}
            </label>
            <UFDoraTextInput
              id={driverNoteId}
              register={register(`${relativeFormPath}.note`)}
              className="flex-grow"
              placeholder={t("shipmentModal:inputs.notePlaceholder")}
              icon={faComment}
            />
          </div>
          <div className="shipment-cargo-form__input-list-row">
            {!hideLoadmeters && (
              <>
                <label
                  className="shipment-cargo-form__input-list-row-label"
                  htmlFor={lmId}
                >
                  {t(
                    liteVersion
                      ? "shipmentModal:inputs.weightLabel"
                      : "shipmentModal:inputs.loadMetersLabel"
                  )}
                  <span aria-hidden>*</span>
                </label>
                <div className="flex-grow">
                  <UFDoraNumberInput
                    id={lmId}
                    placeholder={t(
                      liteVersion
                        ? "shipmentModal:inputs.weightLabel"
                        : "shipmentModal:inputs.loadMetersLabel"
                    )}
                    icon={faGauge}
                    disabled={disableAccumulatedInputs}
                    step={0.01}
                    register={register(`${relativeFormPath}.loadmeters`, {
                      onChange: (e: any) => {
                        const value = String(e.target.value ?? "");
                        const truncated = value.match(/^(-?\d*\.\d{2})\d+$/);
                        if (truncated) {
                          setValue(
                            `${relativeFormPath}.loadmeters`,
                            truncated[1]
                          );
                        }
                      },
                    })}
                    error={currentStopErrors.loadmeters}
                    suffix={liteVersion ? "kg" : ""}
                  />
                  {currentStopErrors.lm && (
                    <div className="shipment-cargo-form__error">
                      {currentStopErrors.lm.message}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {!liteVersion && stopUnitsFeatureEnabled && (
            <div className="shipment-cargo-form__stop-units">
              {isPickup ? (
                <>
                  <div className="shipment-cargo-form__measurement-units-row flex justify-between">
                    <label className="shipment-cargo-form__measurement-units-title">
                      {t("stopUnits:stopUnits")}
                    </label>
                  </div>

                  <div className="shipment-cargo-form__stop-units-table">
                    <div className="stop-unit-line-row stop-unit-line-row--header">
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.qty")}
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.unit")}
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.description")}
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.colli")}
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.weightAbbr")}
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.dimsAbbr")}
                      </div>
                      <div
                        className="stop-unit-line-row-cell"
                        style={{ display: "flex" }}
                      >
                        <span>V,m</span>
                        <span>
                          <sup>3</sup>
                        </span>
                      </div>
                      <div className="stop-unit-line-row-cell">
                        {t("stopUnits:columns.actions")}
                      </div>
                    </div>
                    {currentStopUnits.map((unit) => {
                      const unitIndex = stopUnitsWatch.findIndex(
                        (u: StopUnit) => u.id === unit.id
                      );
                      return (
                        <StopUnitRow
                          key={unit.id}
                          stopUnit={unit}
                          isNew={false}
                          canEdit={true}
                          onEditingChange={(isEditing) =>
                            onStopUnitEditingChange(unit.id, isEditing)
                          }
                          onDelete={() => removeStopUnit(unitIndex)}
                          onSaveStopUnit={(data) =>
                            onSaveStopUnit(data, unitIndex)
                          }
                        />
                      );
                    })}
                    {creatingNewStopUnit && (
                      <StopUnitRow
                        stopUnit={{
                          ...newUnit,
                          pickupId: currentStopReferenceId,
                        }}
                        isNew={true}
                        canEdit={true}
                        onCancelClick={() => setCreatingNewStopUnit(false)}
                        onDelete={() => setCreatingNewStopUnit(false)}
                        onSaveStopUnit={(data) => onSaveStopUnit(data, null)}
                      />
                    )}
                    <div className="mt-2">
                      <DoraButton
                        variant="ghost"
                        onClick={onCreateStopUnitClick}
                        disabled={
                          !userSelectableStopUnitTemplates.length ||
                          creatingNewStopUnit ||
                          editingStopUnitIds.length > 0
                        }
                      >
                        <FontAwesomeIcon
                          icon={faPlus}
                          style={{ marginRight: 8 }}
                        />
                        {t("components:buttonLabels.add")}
                      </DoraButton>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="shipment-cargo-form__measurement-units-row">
                    <label className="shipment-cargo-form__measurement-units-title">
                      {t("stopUnits:stopUnits")}
                    </label>
                  </div>
                  {currentStopUnits.length > 0 && (
                    <div className="shipment-cargo-form__stop-units-table">
                      <div className="stop-unit-line-row stop-unit-line-row--header">
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.qty")}
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.unit")}
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.description")}
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.colli")}
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.weightAbbr")}
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.dimsAbbr")}
                        </div>
                        <div
                          className="stop-unit-line-row-cell"
                          style={{ display: "flex" }}
                        >
                          <span>V,m</span>
                          <sup>3</sup>
                        </div>
                        <div className="stop-unit-line-row-cell">
                          {t("stopUnits:columns.actions")}
                        </div>
                      </div>
                      {currentStopUnits.map((unit) => {
                        const unitIndex = stopUnitsWatch.findIndex(
                          (u: StopUnit) => u.id === unit.id
                        );
                        const unitTemplate = allUnitTemplates.find(
                          (ut) => ut.id === unit.stopUnitTemplateId
                        );
                        return (
                          <div key={unit.id} className="stop-unit-line-row">
                            <StopUnitContent
                              unit={{
                                ...unit,
                                name: unitTemplate?.name || "",
                              }}
                            />
                            <div className="stop-unit-template-line-row-cell shipment-cargo-form__stop-unit-actions">
                              <DoraButton
                                variant="ghost"
                                onClick={() =>
                                  onRemoveUnitFromDropoff(unitIndex)
                                }
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </DoraButton>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {availablePickupUnits.length > 0 && (
                    <Alert
                      severity="info"
                      action={
                        <DoraButton
                          variant="primary-filled"
                          onClick={() => setShowAssignUnitsModal(true)}
                        >
                          {t("stopUnits:assignDialog.assignButton")}
                        </DoraButton>
                      }
                    >
                      {t("stopUnits:assignDialog.unassignedAlert")}
                    </Alert>
                  )}

                  {showAssignUnitsModal && (
                    <AssignStopUnitsDialog
                      availablePickupUnits={availablePickupUnits}
                      stopUnitsWatch={stopUnitsWatch}
                      onAssign={onAssignUnitToDropoff}
                      onClose={() => setShowAssignUnitsModal(false)}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {!liteVersion && (
          <>
            <div className="shipment-cargo-form__measurement-units">
              {!stopUnitsFeatureEnabled && (
                <>
                  <div>
                    <div className="shipment-cargo-form__measurement-units-row">
                      <label className="shipment-cargo-form__measurement-units-label">
                        {t("shipmentModal:inputs.measurementsLabel")}
                      </label>

                      {shouldShowMeasurements ? (
                        <div className="flex justify-end w-full">
                          <DoraLinkButton onClick={onDeleteMeasurementsClick}>
                            {t("components:buttonLabels.delete")}
                          </DoraLinkButton>
                        </div>
                      ) : (
                        <DoraButton
                          onClick={onMeasurementsButtonClick}
                          disabled={disableAccumulatedInputs}
                          variant={"ghost"}
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            style={{ marginRight: 8 }}
                          />
                          {t("shipmentModal:inputs.addMeasurements")}
                        </DoraButton>
                      )}
                    </div>
                    {shouldShowMeasurements && (
                      <div className="shipment-cargo-form__measurements-wrapper">
                        <div>
                          <label htmlFor={colliId}>
                            {t("shipmentModal:inputs.colli")}
                          </label>
                          <UFDoraNumberInput
                            id={colliId}
                            register={register(`${relativeFormPath}.colli`)}
                            disabled={disableAccumulatedInputs}
                            placeholder={t("shipmentModal:inputs.colli")}
                          />
                        </div>
                        <div>
                          <label htmlFor={weightId}>
                            {t("shipmentModal:inputs.weightLabel")}
                          </label>
                          <UFDoraNumberInput
                            id={weightId}
                            register={register(`${relativeFormPath}.weight`)}
                            disabled={disableAccumulatedInputs}
                            suffix={"kg"}
                            placeholder={t("shipmentModal:inputs.weightLabel")}
                          />
                        </div>
                        <div>
                          <label htmlFor={cubicMetersId}>
                            {t("shipmentModal:inputs.cubicMetersLabel")}
                          </label>
                          <UFDoraNumberInput
                            id={cubicMetersId}
                            register={register(
                              `${relativeFormPath}.cubicMeters`
                            )}
                            disabled={disableAccumulatedInputs}
                            suffix={"m³"}
                            placeholder={t(
                              "shipmentModal:inputs.cubicMetersLabel"
                            )}
                          />
                        </div>
                        <div>
                          <label htmlFor={lengthId}>
                            {t("shipmentModal:inputs.lengthLabel")}
                          </label>
                          <UFDoraNumberInput
                            id={lengthId}
                            register={register(`${relativeFormPath}.length`)}
                            suffix={"cm"}
                            placeholder={t("shipmentModal:inputs.lengthLabel")}
                          />
                        </div>
                        <div>
                          <label htmlFor={widthId}>
                            {t("shipmentModal:inputs.widthLabel")}
                          </label>
                          <UFDoraNumberInput
                            id={widthId}
                            register={register(`${relativeFormPath}.width`)}
                            suffix={"cm"}
                            placeholder={t("shipmentModal:inputs.widthLabel")}
                          />
                        </div>
                        <div>
                          <label htmlFor={heightId}>
                            {t("shipmentModal:inputs.heightLabel")}
                          </label>
                          <UFDoraNumberInput
                            id={heightId}
                            register={register(`${relativeFormPath}.height`)}
                            suffix={"cm"}
                            placeholder={t("shipmentModal:inputs.heightLabel")}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="shipment-cargo-form__measurement-units-row">
                      <label className="shipment-cargo-form__measurement-units-label">
                        {t("shipmentModal:inputs.units")}
                      </label>
                      {unitsWatch?.length ? (
                        <div className="flex justify-end w-full">
                          <DoraLinkButton
                            onClick={onDeleteUnitsClick}
                            disabled={disableAccumulatedInputs}
                          >
                            {t("components:buttonLabels.delete")}
                          </DoraLinkButton>
                        </div>
                      ) : (
                        <DoraButton
                          variant={"ghost"}
                          disabled={disableAccumulatedInputs}
                          onClick={onAddUnitsButtonClick}
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            style={{ marginRight: 8 }}
                          />
                          {t("shipmentModal:inputs.addUnits")}
                        </DoraButton>
                      )}
                    </div>
                  </div>

                  {units.length > 0 && (
                    <>
                      <div className="shipment-cargo-form__units-table-wrapper">
                        <div className="shipment-cargo-form__units-table-header-cell">
                          {t("shipmentModal:inputs.unitsQty")}
                        </div>
                        <div className="shipment-cargo-form__units-table-header-cell"></div>
                        <div className="shipment-cargo-form__units-table-header-cell">
                          {t("shipmentModal:inputs.unitsItem")}
                        </div>
                        <div className="shipment-cargo-form__units-table-header-cell"></div>

                        {unitsWatch.map((_: any, index: any) => {
                          const v = getValues(
                            `${relativeFormPath}.pieces[${index}].unit`
                          );
                          const dropdownKeyForChangeDetection = `dropdown-${index}-${v}`;
                          return (
                            <Fragment key={index}>
                              <div>
                                <UFDoraNumberInput
                                  register={register(
                                    `${relativeFormPath}.pieces[${index}].quantity`,
                                    {
                                      valueAsNumber: true,
                                    }
                                  )}
                                  disabled={disableAccumulatedInputs}
                                  className="shipment-cargo-units-table__qty"
                                  error={
                                    currentStopErrors.pieces?.[index]?.quantity
                                  }
                                />
                              </div>
                              <div className="flex items-center">
                                <FontAwesomeIcon
                                  className="shipment-cargo-units-table__times"
                                  icon={faTimes}
                                />
                              </div>
                              <div key={dropdownKeyForChangeDetection}>
                                <UFDoraSelect
                                  control={control}
                                  name={`${relativeFormPath}.pieces[${index}].unit`}
                                  fullWidth
                                  className="shipment-cargo-units-table__dropdown"
                                  options={unitsTypes}
                                  error={
                                    currentStopErrors.pieces?.[index]?.type
                                  }
                                  disabled={disableAccumulatedInputs}
                                />
                              </div>
                              <DoraButton
                                variant="ghost"
                                className="shipment-cargo-units-table__trash-icon"
                                onClick={onDeleteUnitClick(index)}
                                disabled={disableAccumulatedInputs}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </DoraButton>
                            </Fragment>
                          );
                        })}
                      </div>
                      <div>
                        <DoraButton
                          variant={"ghost"}
                          onClick={onAddUnitsButtonClick}
                          disabled={disableAccumulatedInputs}
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            style={{ marginRight: 8 }}
                          />
                          {t("shipmentModal:inputs.addMoreUnits")}
                        </DoraButton>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {!liteVersion && (
          <div className="shipment-cargo-form__documents">
            <div className="flex items-center">
              <label>{t("shipmentModal:inputs.documentsLabel")}</label>
              {!!stopId && (
                <Dropzone
                  maxSize={50000000}
                  accept={{
                    "image/png": [".png"],
                    "image/jpeg": [".jpeg", ".jpg"],
                    "application/pdf": [".pdf"],
                  }}
                  onDrop={onUploadDocumentsClick}
                  disabled={documentsChangeInProgress}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <DoraButton
                        variant="ghost"
                        disabled={documentsChangeInProgress}
                      >
                        <FontAwesomeIcon
                          icon={faPlus}
                          style={{ marginRight: 8 }}
                        />
                        {t("shipmentModal:cards.documents.upload")}
                      </DoraButton>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
            {!!stopId ? (
              <StopDocumentsList
                stopId={stopId}
                controlsDisabled={documentsChangeInProgress}
                mayReferenceStaleData={stopMayReferenceStaleData}
              />
            ) : (
              <div className="shipment-stop-documents__available-later">
                {t("shipmentModal:inputs.documentsStopAvailableLater")}
              </div>
            )}
          </div>
        )}
        <div className="shipment-cargo-form__bottom-border"></div>
      </div>
    </>
  );
};

export default StopEdit;
